import React from 'react';
import { renderValue, isEmptyValue } from '../../helpers/renderValue';

/**
 * Two-column metadata table driven entirely by the backend `document_display`
 * expander — no field list lives in the frontend. Rows may carry a
 * backend-computed `value` (e.g. Preparing Activity = creators[0]), and
 * `name` may be a list of fields whose values join with ", " (e.g. the
 * Delegation "Delegant, Position" rows). Empty fields are dropped so a
 * sparse document doesn't render blank rows.
 *
 * Markup is CONTRACT B's `.prc-table` shape (see /tmp/ds-ref/PORT-SPEC.md on
 * the VM) so metadata rows pick up the design's dark-mode-safe cell tokens
 * instead of Semantic UI's default white table background.
 */

function resolve(row, content) {
  if ('value' in row) return row.value;
  if (Array.isArray(row.name)) {
    const parts = row.name
      .map((n) => content[n])
      .filter((v) => !isEmptyValue(v))
      .map((v) =>
        v && typeof v === 'object' ? v.title || v.token || null : v,
      )
      .filter(Boolean);
    return parts.length ? parts.join(', ') : null;
  }
  return content[row.name];
}

const MetadataTable = ({ content }) => {
  const display = content['@components']?.document_display;
  const rows = display?.metadata || [];
  const visible = rows
    .map((r) => ({ ...r, resolved: resolve(r, content) }))
    .filter((r) => !isEmptyValue(r.resolved));
  if (visible.length === 0) return null;
  return (
    <div className="prc-table-wrap doc-metadata">
      <table className="prc-table">
        <caption className="prc-visually-hidden">Document metadata</caption>
        <thead>
          <tr>
            <th data-align="left" scope="col">
              Field
            </th>
            <th data-align="left" scope="col">
              Value
            </th>
          </tr>
        </thead>
        <tbody>
          {visible.map((r) => {
            const key = Array.isArray(r.name) ? r.name.join('+') : r.name;
            return (
              <tr key={key}>
                <td data-align="left" data-title="">
                  {r.label}
                </td>
                {/* data-title="" suppresses the stacked-card "VALUE"
                    prefix on phones -- the field cell above already names
                    the row, so the label was pure noise nine times over. */}
                <td data-align="left" data-label="Value" data-title="">
                  {renderValue(r.resolved, r.format)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default MetadataTable;
